import {
  Angle,
  DimensionSide,
  Element,
  ElementType,
  Fixture as FixtureElement,
  getThickness,
  getThicknessAnchor,
  isRectLikeElement,
  Polyline,
  Rect,
  RectLikeElement,
  ThicknessAnchor,
  Triangle,
} from '@ritabygglov/core';
import { CONNECTING_BORDER_SIZE, WALL_LINE_WEIGHT } from '../constants';
import { ROTATE_HANDLE_SIZE_PX } from './handleTokens';

import {
  fixtureRegistry,
  NormalizedPoint,
  resolveSnapTemplate,
} from '../services/fixtureRegistry';
import {
  Coordinate,
  Corner,
  Edge,
  isEdge,
  LineSegment,
  ResizeAnchor,
  Vector,
} from '../types';
import { isPointInsideRect } from './intersect';

export enum MouseButtons {
  LEFT = 0,
  MIDDLE = 1,
  RIGHT = 2,
}

export const makeResizeCursorUrl = (angleDeg: number): string => {
  const a = ((angleDeg % 180) + 180) % 180;
  const arrow = 'M5,8 L1,10 L5,12 Z M1,10 L19,10 M15,8 L19,10 L15,12 Z';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"><g transform="rotate(${a},10,10)"><path d="${arrow}" stroke="white" stroke-width="4" fill="white" stroke-linecap="round" stroke-linejoin="round"/><path d="${arrow}" stroke="#1a1a1a" stroke-width="1.75" fill="#1a1a1a" stroke-linecap="round" stroke-linejoin="round"/></g></svg>`;
  return `url('data:image/svg+xml;base64,${btoa(svg)}') 10 10, ew-resize`;
};

export const makeRadiusCursorUrl = (): string => {
  const pointer =
    'M2 2 L2 15 L5.5 11.9 L7.7 16.8 L10.1 15.7 L7.9 10.9 L12.5 10.9 Z';
  const arc = 'M14.2 21.8 A5 5 0 0 1 19.8 16.2';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path d="${pointer}" stroke="white" stroke-width="2.5" fill="white" stroke-linejoin="round"/><path d="${arc}" stroke="white" stroke-width="4" fill="none" stroke-linecap="round"/><path d="${pointer}" fill="#1a1a1a"/><path d="${arc}" stroke="#1a1a1a" stroke-width="1.75" fill="none" stroke-linecap="round"/></svg>`;
  return `url('data:image/svg+xml;base64,${btoa(svg)}') 2 2, default`;
};

export const getClosestCornerById = (
  element: Element,
  id: string
): ResizeAnchor | number | null => {
  switch (element.type) {
    case ElementType.Polyline: {
      const parts = id.split('-');
      const pointIndex = parts[parts.length - 1];
      return Number(pointIndex);
    }
    case ElementType.Text:
    case ElementType.Symbol:
    case ElementType.Fixture:
    case ElementType.Rect:
    case ElementType.Triangle:
    case ElementType.Trapezoid:
    case ElementType.Angle: {
      const parts = id.split('-');
      const last = parts[parts.length - 1];
      const secondLast = parts[parts.length - 2];

      const edgeNames = ['top', 'right', 'bottom', 'left'];
      if (secondLast === 'resize' && edgeNames.includes(last)) {
        return (last[0].toUpperCase() + last.slice(1)) as Edge;
      }

      const horizontal = secondLast;
      const vertical = last;
      return (horizontal[0].toUpperCase() +
        horizontal.slice(1) +
        vertical[0].toUpperCase() +
        vertical.slice(1)) as Corner;
    }
    default: {
      console.error('Could not find corner');
      return null;
    }
  }
};

/**
 * Returns the edge's coordinates, the unit vector the edge
 * moves in, and an anchor point on the edge for elementDraging purposes.
 */
export const getEdgeDragGeometry = (
  element: Element,
  edge: Edge
): {
  edgePoints: Coordinate[];
  freeAxis: Vector;
  edgeAnchor: Coordinate;
} | null => {
  if (element.type === ElementType.Polyline) return null;
  const rect = element as Rect;

  const { x, y, width, height } = rect;
  const rotation = rect.rotate || 0;
  const center: Coordinate = { x: x + width / 2, y: y + height / 2 };
  const radians = rotation * (Math.PI / 180);

  let localAxis: Vector;
  let corners: [Coordinate, Coordinate];

  switch (edge) {
    case Edge.Left:
      localAxis = { x: 1, y: 0 };
      corners = [
        { x, y },
        { x, y: y + height },
      ];
      break;
    case Edge.Right:
      localAxis = { x: 1, y: 0 };
      corners = [
        { x: x + width, y },
        { x: x + width, y: y + height },
      ];
      break;
    case Edge.Top:
      localAxis = { x: 0, y: 1 };
      corners = [
        { x, y },
        { x: x + width, y },
      ];
      break;
    case Edge.Bottom:
      localAxis = { x: 0, y: 1 };
      corners = [
        { x, y: y + height },
        { x: x + width, y: y + height },
      ];
      break;
  }

  //convert to global coordinate system rotation
  const freeAxis: Coordinate = {
    x: localAxis.x * Math.cos(radians) - localAxis.y * Math.sin(radians),
    y: localAxis.x * Math.sin(radians) + localAxis.y * Math.cos(radians),
  };

  const edgePoints = corners.map((corner) =>
    rotatePointAroundCenter(corner, center, rotation)
  );

  const edgeAnchor: Coordinate = {
    x: (edgePoints[0].x + edgePoints[1].x) / 2,
    y: (edgePoints[0].y + edgePoints[1].y) / 2,
  };

  return { edgePoints, freeAxis, edgeAnchor };
};

export function rotateCenter(
  x: number,
  y: number,
  cx: number,
  cy: number,
  angle: number
) {
  return [
    (x - cx) * Math.cos(angle * (Math.PI / 180)) -
      (y - cy) * Math.sin(angle * (Math.PI / 180)) +
      cx,
    (x - cx) * Math.sin(angle * (Math.PI / 180)) +
      (y - cy) * Math.cos(angle * (Math.PI / 180)) +
      cy,
  ];
}

const projectEdgeCursor = (
  selectedEdge: Edge,
  clientX: number,
  clientY: number,
  x: number,
  y: number,
  cx: number,
  cy: number,
  w: number,
  h: number,
  rotate: number
): { syntheticCorner: Corner; projectedX: number; projectedY: number } => {
  const [localX, localY] = rotateCenter(clientX, clientY, cx, cy, -rotate);

  let constrainedLocalX = localX;
  let constrainedLocalY = localY;
  let syntheticCorner: Corner;

  switch (selectedEdge) {
    case Edge.Right:
      constrainedLocalY = y + h;
      syntheticCorner = Corner.BottomRight;
      break;
    case Edge.Left:
      constrainedLocalY = y;
      syntheticCorner = Corner.TopLeft;
      break;
    case Edge.Top:
      constrainedLocalX = x;
      syntheticCorner = Corner.TopLeft;
      break;
    case Edge.Bottom:
      constrainedLocalX = x + w;
      syntheticCorner = Corner.BottomRight;
      break;
  }

  const [projectedX, projectedY] = rotateCenter(
    constrainedLocalX,
    constrainedLocalY,
    cx,
    cy,
    rotate
  );

  return { syntheticCorner, projectedX, projectedY };
};

export const resizeRect = (
  selectedAnchor: ResizeAnchor,
  clientX: number,
  clientY: number,
  rect: {
    x: number;
    y: number;
    width: number;
    height: number;
    rotate: number;
  }
) => {
  const { x, y, width: w, height: h, rotate } = rect;

  const cx = x + w / 2;
  const cy = y + h / 2;

  let effectiveCorner: Corner;
  let effectiveX: number;
  let effectiveY: number;

  if (isEdge(selectedAnchor)) {
    const projected = projectEdgeCursor(
      selectedAnchor,
      clientX,
      clientY,
      x,
      y,
      cx,
      cy,
      w,
      h,
      rotate
    );
    effectiveCorner = projected.syntheticCorner;
    effectiveX = projected.projectedX;
    effectiveY = projected.projectedY;
  } else {
    effectiveCorner = selectedAnchor;
    effectiveX = clientX;
    effectiveY = clientY;
  }

  // eslint-disable-next-line
  const [newWidth, newHeight, newX, newY, _newCX, _newCY] =
    resizeRotatedRectangle({
      x,
      y,
      cx,
      cy,
      w,
      h,
      rotate,
      selectedCorner: effectiveCorner,
      clientX: effectiveX,
      clientY: effectiveY,
    });
  return [newWidth, newHeight, newX, newY];
};

export const constrainCornerToAspectRatio = (
  selectedCorner: Corner,
  clientX: number,
  clientY: number,
  rect: {
    x: number;
    y: number;
    width: number;
    height: number;
    rotate: number;
  }
): Coordinate => {
  const { x, y, width: w, height: h, rotate } = rect;
  const cx = x + w / 2;
  const cy = y + h / 2;

  const corners: Record<Corner, [number, number]> = {
    [Corner.TopLeft]: [x, y],
    [Corner.TopRight]: [x + w, y],
    [Corner.BottomRight]: [x + w, y + h],
    [Corner.BottomLeft]: [x, y + h],
  };
  const opposite: Record<Corner, Corner> = {
    [Corner.TopLeft]: Corner.BottomRight,
    [Corner.TopRight]: Corner.BottomLeft,
    [Corner.BottomRight]: Corner.TopLeft,
    [Corner.BottomLeft]: Corner.TopRight,
  };

  const [fixedX, fixedY] = corners[opposite[selectedCorner]];
  const [draggedX, draggedY] = corners[selectedCorner];
  const [ax, ay] = rotateCenter(fixedX, fixedY, cx, cy, rotate);
  const [bx, by] = rotateCenter(draggedX, draggedY, cx, cy, rotate);

  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return { x: clientX, y: clientY };

  const t = ((clientX - ax) * dx + (clientY - ay) * dy) / lengthSquared;
  return { x: ax + t * dx, y: ay + t * dy };
};

export const normalizeRect = (
  width: number,
  height: number,
  x: number,
  y: number
): [number, number, number, number] => {
  const newWidth = Math.max(1, Math.abs(width));
  const newHeight = Math.max(1, Math.abs(height));
  const newX = width < 0 ? x + width : x;
  const newY = height < 0 ? y + height : y;
  return [newWidth, newHeight, newX, newY];
};

export const flipAnchor = (
  anchor: ResizeAnchor,
  flipX: boolean,
  flipY: boolean
): ResizeAnchor => {
  if (!flipX && !flipY) return anchor;

  if (anchor === Edge.Left) return Edge.Right;
  if (anchor === Edge.Right) return Edge.Left;
  if (anchor === Edge.Top) return Edge.Bottom;
  if (anchor === Edge.Bottom) return Edge.Top;

  const flipHorizontal: Record<Corner, Corner> = {
    [Corner.TopLeft]: Corner.TopRight,
    [Corner.TopRight]: Corner.TopLeft,
    [Corner.BottomLeft]: Corner.BottomRight,
    [Corner.BottomRight]: Corner.BottomLeft,
  };
  const flipVertical: Record<Corner, Corner> = {
    [Corner.TopLeft]: Corner.BottomLeft,
    [Corner.TopRight]: Corner.BottomRight,
    [Corner.BottomLeft]: Corner.TopLeft,
    [Corner.BottomRight]: Corner.TopRight,
  };

  let result = anchor as Corner;
  if (flipX) result = flipHorizontal[result];
  if (flipY) result = flipVertical[result];
  return result;
};

export const canNormalizeResize = (
  elementUpdate: Element,
  selectedAnchor: ResizeAnchor | number | null
): elementUpdate is RectLikeElement =>
  isRectLikeElement(elementUpdate) &&
  elementUpdate.type !== ElementType.Text &&
  typeof selectedAnchor !== 'number' &&
  selectedAnchor !== null;

export const applyResizeNormalization = (
  elementUpdate: Element,
  selectedAnchor: ResizeAnchor | number | null
): { normalizedUpdate: Element; newAnchor: ResizeAnchor | null } => {
  if (!canNormalizeResize(elementUpdate, selectedAnchor)) {
    return { normalizedUpdate: elementUpdate, newAnchor: null };
  }

  const flipX = elementUpdate.width < 0;
  const flipY = elementUpdate.height < 0;

  if (!flipX && !flipY) {
    return { normalizedUpdate: elementUpdate, newAnchor: null };
  }

  const [normWidth, normHeight, normX, normY] = normalizeRect(
    elementUpdate.width,
    elementUpdate.height,
    elementUpdate.x,
    elementUpdate.y
  );

  return {
    normalizedUpdate: {
      ...elementUpdate,
      width: normWidth,
      height: normHeight,
      x: normX,
      y: normY,
    },
    newAnchor: flipAnchor(selectedAnchor as ResizeAnchor, flipX, flipY),
  };
};

export const clampRectWidth = (
  width: number,
  height: number,
  x: number,
  y: number,
  selectedAnchor: ResizeAnchor,
  minWidth: number
): [number, number, number, number] => {
  if (selectedAnchor === Edge.Top || selectedAnchor === Edge.Bottom) {
    return [width, height, x, y];
  }
  if (width >= minWidth) {
    return [width, height, x, y];
  }
  const isLeftDrag =
    selectedAnchor === Corner.TopLeft ||
    selectedAnchor === Corner.BottomLeft ||
    selectedAnchor === Edge.Left;
  const newX = isLeftDrag ? x + width - minWidth : x;
  return [minWidth, height, newX, y];
};

export const clampRectHeight = (
  width: number,
  height: number,
  x: number,
  y: number,
  selectedAnchor: ResizeAnchor,
  minHeight: number
): [number, number, number, number] => {
  if (selectedAnchor === Edge.Left || selectedAnchor === Edge.Right) {
    return [width, height, x, y];
  }
  if (height >= minHeight) {
    return [width, height, x, y];
  }
  const isTopDrag =
    selectedAnchor === Corner.TopLeft ||
    selectedAnchor === Corner.TopRight ||
    selectedAnchor === Edge.Top;
  const newY = isTopDrag ? y + height - minHeight : y;
  return [width, minHeight, x, newY];
};

export const clampRotatedRectSize = (
  width: number,
  height: number,
  x: number,
  y: number,
  rotate: number,
  selectedAnchor: ResizeAnchor,
  minWidth: number,
  minHeight: number
): [number, number, number, number] => {
  const [clampedWidth, clampedHeight] = clampRectHeight(
    ...clampRectWidth(width, height, x, y, selectedAnchor, minWidth),
    selectedAnchor,
    minHeight
  );
  if (clampedWidth === width && clampedHeight === height) {
    return [width, height, x, y];
  }

  const isLeftDrag =
    selectedAnchor === Corner.TopLeft ||
    selectedAnchor === Corner.BottomLeft ||
    selectedAnchor === Edge.Left;
  const isTopDrag =
    selectedAnchor === Corner.TopLeft ||
    selectedAnchor === Corner.TopRight ||
    selectedAnchor === Edge.Top;

  const [fixedX, fixedY] = rotateCenter(
    isLeftDrag ? x + width : x,
    isTopDrag ? y + height : y,
    x + width / 2,
    y + height / 2,
    rotate
  );
  const [offsetX, offsetY] = rotateCenter(
    isLeftDrag ? clampedWidth / 2 : -clampedWidth / 2,
    isTopDrag ? clampedHeight / 2 : -clampedHeight / 2,
    0,
    0,
    rotate
  );
  const centerX = fixedX - offsetX;
  const centerY = fixedY - offsetY;

  return [
    clampedWidth,
    clampedHeight,
    centerX - clampedWidth / 2,
    centerY - clampedHeight / 2,
  ];
};

export const setRotatedRectHeight = (
  rect: { x: number; y: number; width: number; height: number },
  rotate: number,
  height: number,
  keepBottom = false
): { x: number; y: number; height: number } => {
  const delta = (height - rect.height) / 2;
  const [shiftX, shiftY] = rotateCenter(
    0,
    keepBottom ? -delta : delta,
    0,
    0,
    rotate
  );
  const centerX = rect.x + rect.width / 2 + shiftX;
  const centerY = rect.y + rect.height / 2 + shiftY;
  return {
    x: centerX - rect.width / 2,
    y: centerY - height / 2,
    height,
  };
};

export const resizeRotatedRectangle = ({
  x,
  y,
  cx,
  cy,
  w,
  h,
  rotate,
  selectedCorner,
  clientX,
  clientY,
}: {
  x: number;
  y: number;
  cx: number;
  cy: number;
  w: number;
  h: number;
  rotate: number;
  selectedCorner: Corner;
  clientX: number;
  clientY: number;
}) => {
  // https://shihn.ca/posts/2020/resizing-rotated-elements/

  /*
  A = top left corner
  B = top right corner
  C = bottom right corner
  D = bottom left corner
  */
  const rotatedA = rotateCenter(x, y, cx, cy, rotate);
  const rotatedB = rotateCenter(x + w, y, cx, cy, rotate);
  const rotatedC = rotateCenter(x + w, y + h, cx, cy, rotate);
  const rotatedD = rotateCenter(x, y + h, cx, cy, rotate);

  if (selectedCorner === Corner.BottomRight) {
    const newCenter = [
      (rotatedA[0] + clientX) / 2,
      (rotatedA[1] + clientY) / 2,
    ];

    const newTopLeft = rotateCenter(
      rotatedA[0],
      rotatedA[1],
      newCenter[0],
      newCenter[1],
      -rotate
    );
    const newBottomRight = rotateCenter(
      clientX,
      clientY,
      newCenter[0],
      newCenter[1],
      -rotate
    );
    const newX = newTopLeft[0];
    const newY = newTopLeft[1];
    const newCX = newCenter[0];
    const newCY = newCenter[1];
    const newWidth = newBottomRight[0] - newTopLeft[0];
    const newHeight = newBottomRight[1] - newTopLeft[1];
    return [newWidth, newHeight, newX, newY, newCX, newCY];
  } else if (selectedCorner === Corner.BottomLeft) {
    const newCenter = [
      (rotatedB[0] + clientX) / 2,
      (rotatedB[1] + clientY) / 2,
    ];

    const newTopRight = rotateCenter(
      rotatedB[0],
      rotatedB[1],
      newCenter[0],
      newCenter[1],
      -rotate
    );
    const newBottomLeft = rotateCenter(
      clientX,
      clientY,
      newCenter[0],
      newCenter[1],
      -rotate
    );
    const newWidth = newTopRight[0] - newBottomLeft[0];
    const newHeight = newBottomLeft[1] - newTopRight[1];
    const newX = newBottomLeft[0];
    const newY = newTopRight[1];
    const newCX = newCenter[0];
    const newCY = newCenter[1];

    return [newWidth, newHeight, newX, newY, newCX, newCY];
  } else if (selectedCorner === Corner.TopRight) {
    const newCenter = [
      (rotatedD[0] + clientX) / 2,
      (rotatedD[1] + clientY) / 2,
    ];

    const newBottomLeft = rotateCenter(
      rotatedD[0],
      rotatedD[1],
      newCenter[0],
      newCenter[1],
      -rotate
    );

    const newTopRight = rotateCenter(
      clientX,
      clientY,
      newCenter[0],
      newCenter[1],
      -rotate
    );
    const newX = newBottomLeft[0];
    const newY = newTopRight[1];
    const newCX = newCenter[0];
    const newCY = newCenter[1];
    const newWidth = newTopRight[0] - newBottomLeft[0];
    const newHeight = newBottomLeft[1] - newTopRight[1];
    return [newWidth, newHeight, newX, newY, newCX, newCY];
  } else {
    // TOP LEFT
    const newCenter = [
      (rotatedC[0] + clientX) / 2,
      (rotatedC[1] + clientY) / 2,
    ];

    const newBottomRight = rotateCenter(
      rotatedC[0],
      rotatedC[1],
      newCenter[0],
      newCenter[1],
      -rotate
    );

    const newTopLeft = rotateCenter(
      clientX,
      clientY,
      newCenter[0],
      newCenter[1],
      -rotate
    );

    const newX = newTopLeft[0];
    const newY = newTopLeft[1];
    const newCX = newCenter[0];
    const newCY = newCenter[1];
    const newWidth = newBottomRight[0] - newTopLeft[0];
    const newHeight = newBottomRight[1] - newTopLeft[1];
    return [newWidth, newHeight, newX, newY, newCX, newCY];
  }
};

export const getMidPoints = (element: Element): [number, number] => {
  if (
    element.type === ElementType.Rect ||
    element.type === ElementType.Text ||
    element.type === ElementType.Symbol ||
    element.type === ElementType.Fixture ||
    element.type === ElementType.Triangle ||
    element.type === ElementType.Trapezoid ||
    element.type === ElementType.Angle
  ) {
    return [element.x + element.width / 2, element.y + element.height / 2];
  } else {
    // TODO: Implement this
    return [0, 0];
  }
};

export function copy<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj;
  }
  return JSON.parse(JSON.stringify(obj));
}

export function angleBetweenPoints(p1: Point, p2: Point): number {
  return Math.atan2(p2.y - p1.y, p2.x - p1.x) * (180 / Math.PI);
}

// Find first overlapping outside border and return that element
export function findOverlappingElement(
  x: number,
  y: number,
  elements: Element[]
) {
  const element = elements.find((e) => {
    switch (e.type) {
      case ElementType.Rect:
      case ElementType.Text:
        return isPointInsideRect(x, y, e, CONNECTING_BORDER_SIZE);
      default:
        return false;
    }
  });

  return element;
}

export function uuid(length = 10): string {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength = characters.length;

  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
}

export function formatNumberOneDecimal(num: number | string) {
  return ((Number(num) * 10) / 10).toFixed(1);
}

export const rotationConverter = (value: string) =>
  // filter degree symbol
  // replace comma
  // remove complete 360 laps
  //round to nearest decimal

  Math.round((Number(value.replace('º', '').replace(',', '.')) % 360) * 10) /
  10;

export const angleConverter = (value: string) =>
  Math.round(Number(value.replace('º', '').replace(',', '.')) * 10) / 10;

export function rotatePoint(
  cx: number,
  cy: number,
  angleRad: number,
  origin: [number, number]
): [number, number] {
  const [ox, oy] = origin;
  const cosAngle = Math.cos(angleRad);
  const sinAngle = Math.sin(angleRad);

  const newX = ox + cosAngle * (cx - ox) - sinAngle * (cy - oy);
  const newY = oy + sinAngle * (cx - ox) + cosAngle * (cy - oy);

  return [newX, newY];
}

/**
 * Rotates a point around a center point by a given angle in degrees
 * @param point - The point to rotate
 * @param center - The center point to rotate around
 * @param angleDegrees - The angle to rotate by in degrees
 * @returns The rotated point
 */
export function rotatePointAroundCenter(
  point: { x: number; y: number },
  center: { x: number; y: number },
  angleDegrees: number
): { x: number; y: number } {
  const angleRad = (angleDegrees * Math.PI) / 180;
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return {
    x: center.x + dx * Math.cos(angleRad) - dy * Math.sin(angleRad),
    y: center.y + dx * Math.sin(angleRad) + dy * Math.cos(angleRad),
  };
}

export function rectangleCorners(
  x: number,
  y: number,
  width: number,
  height: number,
  angleDeg: number
): [number, number][] {
  // Center of the rectangle
  const centerX = x + width / 2;
  const centerY = y + height / 2;

  // Corners of the rectangle without rotation
  const corners = [
    [x, y],
    [x + width, y],
    [x + width, y + height],
    [x, y + height],
  ];

  // Convert angle to radians
  const angleRad = angleDeg * (Math.PI / 180);

  // Rotate each corner around the center
  const rotatedCorners = corners.map((point) =>
    rotatePoint(point[0], point[1], angleRad, [centerX, centerY])
  );

  return rotatedCorners;
}

export function pairNeighbors<T>(items: T[]): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < items.length - 1; i++) {
    result.push([items[i], items[i + 1]]);
  }
  return result;
}

interface Point {
  x: number;
  y: number;
}

function perpendicularVector(
  dx: number,
  dy: number
): { dx: number; dy: number } {
  // Rotate by 90 degrees to get the perpendicular vector
  return { dx: -dy, dy: dx };
}

function normalizeVector(dx: number, dy: number): { dx: number; dy: number } {
  const length = Math.sqrt(dx * dx + dy * dy);
  return { dx: dx / length, dy: dy / length };
}

export function offsetLine(
  point1: Point,
  point2: Point,
  distance: number,
  dimensionSide: DimensionSide
): [number, number][] {
  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;

  // Get the perpendicular direction vector
  const perpVector = perpendicularVector(dx, dy);

  // Normalize the perpendicular vector
  const normPerpVector = normalizeVector(perpVector.dx, perpVector.dy);

  // Determine the direction of the offset
  const directionMultiplier = dimensionSide === DimensionSide.Above ? 1 : -1;
  // const directionMultiplier = dimensionSide === DimensionSide.Above ? 1 : -1;

  // Calculate the offset points
  const offsetX = normPerpVector.dx * distance * directionMultiplier;
  const offsetY = normPerpVector.dy * distance * directionMultiplier;

  const point3: [number, number] = [point1.x + offsetX, point1.y + offsetY];
  const point4: [number, number] = [point2.x + offsetX, point2.y + offsetY];

  const halfLengthDx = (point2.x - point1.x) / 2;
  const halfLengthDy = (point2.y - point1.y) / 2;

  const textMargin = 10;
  const textCenterBaseX = point1.x + halfLengthDx + offsetX;
  const textCenterBaseY = point1.y + halfLengthDy + offsetY;
  const textMarginOffsetX =
    normPerpVector.dx * textMargin * directionMultiplier;
  const textMarginOffsetY =
    normPerpVector.dy * textMargin * directionMultiplier;

  const point5: [number, number] = [
    textCenterBaseX + textMarginOffsetX,
    textCenterBaseY + textMarginOffsetY,
  ];

  return [point3, point4, point5];
}

export function getDistance(point1: Point, point2: Point) {
  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;

  // Calculate the length of the original line
  const originalLength = Math.sqrt(dx * dx + dy * dy);
  return originalLength;
}

//dåligt namn, kommer inte fatta om 6 mån, byt
export function computeNewLineEndpoint(
  p0: Coordinate,
  p1: Coordinate,
  newLength: number
): Coordinate | null {
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const currentLength = Math.sqrt(dx * dx + dy * dy);
  if (currentLength === 0) return null;
  const ux = dx / currentLength;
  const uy = dy / currentLength;
  return {
    x: p0.x + ux * newLength,
    y: p0.y + uy * newLength,
  };
}

export function resolvePolylinePoint(polyline: Polyline, index: number) {
  const point = polyline.points[index];
  if (!point) return null;
  const resolved =
    point.connectingPointIndex !== undefined
      ? polyline.points[point.connectingPointIndex]
      : point;
  return resolved ?? null;
}

export function resizePolylineSegment(
  polyline: Polyline,
  segmentIndex: number,
  newLength: number
): Polyline | null {
  const p0 = resolvePolylinePoint(polyline, segmentIndex);
  const p1 = resolvePolylinePoint(polyline, segmentIndex + 1);
  if (!p0 || !p1) return null;

  const newP1 = computeNewLineEndpoint(
    { x: p0.x, y: p0.y },
    { x: p1.x, y: p1.y },
    newLength
  );
  if (!newP1) return null;

  const el = copy(polyline);
  const endpointIndex = segmentIndex + 1;
  const currentEndpoint = el.points[endpointIndex];
  if (!currentEndpoint) return null;

  const dx = newP1.x - currentEndpoint.x;
  const dy = newP1.y - currentEndpoint.y;
  // Detach any snap on the moved endpoint so it sits at the typed length,
  // and translate every subsequent point by the same delta so later
  // segments keep their shape.
  const newPoints = el.points.map((point, i) => {
    if (i < endpointIndex) return point;
    if (i === endpointIndex) {
      return {
        ...point,
        x: newP1.x,
        y: newP1.y,
        connectingPointIndex: undefined,
      };
    }
    return {
      ...point,
      x: point.x + dx,
      y: point.y + dy,
      connectingPointIndex: undefined,
    };
  });

  return { ...el, points: newPoints };
}

export function calculateAngle(point1: Point, point2: Point): number {
  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;
  return Math.atan2(dy, dx); // Angle in radians
}

export function radiansToDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}

export const getEdgePerpAngleDeg = (
  from: { x: number; y: number },
  to: { x: number; y: number },
  elementRotateDeg: number
): number => {
  const edgeAngleDeg = radiansToDegrees(
    Math.atan2(to.y - from.y, to.x - from.x)
  );
  return elementRotateDeg + edgeAngleDeg + 90;
};

/**
 * Snaps a point to the nearest angle increment relative to absolute screen coordinates.
 */
export function snapPointToAngle(
  fixedPoint: Point,
  movablePoint: Point,
  radians = Math.PI / 12 // 15 degrees
): Point {
  const dx = movablePoint.x - fixedPoint.x;
  const dy = movablePoint.y - fixedPoint.y;

  const distance = Math.sqrt(dx * dx + dy * dy); // Distance between points
  const angle = Math.atan2(dy, dx); // Angle of the line in radians

  // Snap angle to the nearest 15 degrees (PI / 12 radians)
  const snappedAngle = Math.round(angle / radians) * radians;

  // Calculate the new snapped point
  return {
    x: fixedPoint.x + distance * Math.cos(snappedAngle),
    y: fixedPoint.y + distance * Math.sin(snappedAngle),
  };
}

function roundToNearest15(angle: number): number {
  return Math.round(angle / 15) * 15;
}

function distanceBetweenPoints(p1: Point, p2: Point): number {
  return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
}

/**
 * Snaps a point (p3) to the nearest 15° angle increment relative to the previous line segment (p1->p2)
 */
export function closestAlignedPoint(p1: Point, p2: Point, p3: Point): Point {
  const baseAngle = angleBetweenPoints(p1, p2); // Direction of p1 → p2
  const p2p3Angle = angleBetweenPoints(p2, p3); // Angle of p2 → p3

  const relativeAngle = p2p3Angle - baseAngle; // Angle difference from p1 → p2
  const adjustedRelativeAngle = roundToNearest15(relativeAngle); // Snap to 15° increments
  const finalAngle = baseAngle + adjustedRelativeAngle; // Adjusted absolute angle

  const distance = distanceBetweenPoints(p2, p3); // Preserve original p2 → p3 distance
  const radians = finalAngle * (Math.PI / 180);

  return {
    x: p2.x + Math.cos(radians) * distance,
    y: p2.y + Math.sin(radians) * distance,
  };
}

/**
 * Takes a point and a vector. Returns an array of new points
 * where the vector is applied in all four directions from that point
 */
function getSuggestedPoints(dx: number, dy: number, point: Point) {
  // Extend this vector forward from the last point
  const extendedPoint: Point = {
    x: point.x - dx,
    y: point.y - dy,
  };

  const extendedPoint2: Point = {
    x: point.x + dx,
    y: point.y + dy,
  };

  // perpendicular 1
  const extendedPoint3: Point = {
    x: point.x - dy,
    y: point.y + dx,
  };

  // perpendicular 2
  const extendedPoint4: Point = {
    x: point.x + dy,
    y: point.y - dx,
  };

  const extendedPoints = [
    extendedPoint,
    extendedPoint2,
    extendedPoint3,
    extendedPoint4,
  ];
  return extendedPoints;
}

/**
 * Returns polyline segment indexes closest to the currently selected point
 */
export function getAdjacentPointIndices(
  elementPoints: Point[],
  position: number
) {
  const adjacentPointIndices: number[] = [];

  if (elementPoints.length == 2) {
    // Polyline has one line segment. Return opsite point of currently selected one.

    adjacentPointIndices[0] = position === 0 ? 1 : 0;

    return adjacentPointIndices;
  } else {
    // Polyline has multiple line segments

    if (position === 0) {
      // first point selected, segment is "to the right"
      [adjacentPointIndices[0], adjacentPointIndices[1]] = [1, 2];
    } else if (position === 1) {
      // second point selected, snap around first point'
      [adjacentPointIndices[0], adjacentPointIndices[1]] = [0, 0];
    } else if (position > 0) {
      // pick segment closest "to the left"
      [adjacentPointIndices[0], adjacentPointIndices[1]] = [
        position - 2,
        position - 1,
      ];
    }

    return adjacentPointIndices;
  }
}

/**
 * Finds a snap point that aligns the new segment with a previous segment's direction.
 *
 * The function iterates through all previous segments, projects guide lines from the
 * last vertex in each segment's direction, and returns the closest projected point
 * if it falls within the snap radius.
 *
 */
export function getAlignedNextPoint(
  points: Point[],
  newPoint: Point,
  prevIndex: number,
  radius = 20
) {
  if (points.length < 3) throw new Error('At least two points are required.');

  let closestPoint: Point | undefined;
  let minDistance = Infinity;
  const lastPoint = points[prevIndex];

  // Iterate over all previous vectors
  for (let i = 0; i < points.length - 2; i++) {
    const pA = points[i];
    const pB = points[i + 1];

    if (pA === newPoint || pB === newPoint) continue;

    // Compute vector from pA -> pB
    const dx = pB.x - pA.x;
    const dy = pB.y - pA.y;

    const extendedPoints = getSuggestedPoints(dx, dy, lastPoint);
    extendedPoints.forEach((eP) => {
      const dist = distanceBetweenPoints(newPoint, eP);
      if (dist < minDistance && dist <= radius) {
        minDistance = dist;
        closestPoint = eP;
      }
    });
  }

  // If a valid alignment is found, return it; otherwise, return the original input
  return closestPoint;
}

function withInBounds(p1: Point, p2: Point) {
  return Math.abs(p1.x - p2.x) < 20 || Math.abs(p1.y - p2.y) < 20;
}

/**
 * Aligns 90 degrees to one or more lines if the point is within 15 degree angle such line
 */
export function snapToSeveralPoints(pos: number, points: Point[]) {
  const current = points[pos];

  return points
    .filter((_, i) => pos !== i)
    .filter((p) => withInBounds(current, p))
    .reduce((acc, cur) => snapPointToAngle(cur, acc, Math.PI / 4), current);
}

export function isConnected(coordinates: [number, number, unknown][]) {
  const start = coordinates[0];
  const end = coordinates[coordinates.length - 1];
  return start[0] === end[0] && start[1] === end[1];
}

export function getWallLineWeight(thickness: number): number {
  return Math.min(WALL_LINE_WEIGHT, thickness / 3);
}

export function getInnerWallStrokeWidth(thickness: number): number {
  return Math.max(thickness - 2 * getWallLineWeight(thickness), 0);
}

export function calculateCrossingLine(
  a: [number, number, boolean],
  b: [number, number, boolean],
  length: number
): [number, number, boolean][] {
  const angleAB = Math.atan2(b[1] - a[1], b[0] - a[0]);
  const angle45 = angleAB + Math.PI / 3;

  const halfLength = length / 2;

  // Calculate start point before A
  const x1 = a[0] - halfLength * Math.cos(angle45);
  const y1 = a[1] - halfLength * Math.sin(angle45);

  // Calculate end point after A
  const x2 = a[0] + halfLength * Math.cos(angle45);
  const y2 = a[1] + halfLength * Math.sin(angle45);

  return [
    [x1, y1, false],
    [x2, y2, false],
  ];
}

export function getCrossingPoints(points: [number, number, boolean][]) {
  const res: [[number, number, boolean], [number, number, boolean]][] = [];
  if (points.length < 2) {
    return res;
  }

  for (let j = 0; j < points.length - 1; j++) {
    res.push([points[j], points[j + 1]]);
  }
  res.push([points[points.length - 1], points[points.length - 2]]);

  return res;
}

export function getTriApexHandlePosition(triangle: Triangle | Angle) {
  const apexX: number = triangle.x + triangle.pointRatio * triangle.width;
  const apexY: number = triangle.y;

  return { apexX, apexY };
}

export function getCornerBisector(
  prev: Coordinate,
  corner: Coordinate,
  next: Coordinate
) {
  const inDx = prev.x - corner.x;
  const inDy = prev.y - corner.y;
  const segInLen = Math.sqrt(inDx * inDx + inDy * inDy);

  const outDx = next.x - corner.x;
  const outDy = next.y - corner.y;
  const segOutLen = Math.sqrt(outDx * outDx + outDy * outDy);

  if (segInLen === 0 || segOutLen === 0) return null;

  const bisX = inDx / segInLen + outDx / segOutLen;
  const bisY = inDy / segInLen + outDy / segOutLen;
  const bisLen = Math.sqrt(bisX * bisX + bisY * bisY);
  if (bisLen === 0) return null;

  return {
    bisDirX: bisX / bisLen,
    bisDirY: bisY / bisLen,
    segInLen,
    segOutLen,
    maxRadius: Math.min(segInLen, segOutLen) / 2,
  };
}

/**
 * Offsets a convex polygon by `distance` along each vertex bisector, scaled by
 * 1/sin(θ/2) so the offset edges stay parallel to the originals. A positive
 * distance moves the outline outward, a negative one inward.
 *
 * Convex input only — the bisector construction self-intersects on reflex
 * corners. For polylines, offset each segment independently instead.
 */
export function offsetPolygon(
  vertices: Coordinate[],
  distance: number
): Coordinate[] {
  if (distance === 0 || vertices.length < 3) return vertices;

  // Positive distance must move away from the centroid regardless of winding;
  // the bisector points into the polygon from a corner.
  const centroid = {
    x: vertices.reduce((sum, v) => sum + v.x, 0) / vertices.length,
    y: vertices.reduce((sum, v) => sum + v.y, 0) / vertices.length,
  };

  return vertices.map((corner, i) => {
    const prev = vertices[(i - 1 + vertices.length) % vertices.length];
    const next = vertices[(i + 1) % vertices.length];
    const bisector = getCornerBisector(prev, corner, next);
    if (!bisector) return corner;

    const { bisDirX, bisDirY } = bisector;

    // Half the corner angle: the bisector's angle to either adjacent edge.
    const inDirX = (prev.x - corner.x) / bisector.segInLen;
    const inDirY = (prev.y - corner.y) / bisector.segInLen;
    const cosHalf = Math.min(
      1,
      Math.max(-1, inDirX * bisDirX + inDirY * bisDirY)
    );
    const sinHalf = Math.sqrt(Math.max(0, 1 - cosHalf * cosHalf));
    if (sinHalf === 0) return corner;

    // The bisector points inward for a convex corner; flip when it does not.
    const pointsInward =
      bisDirX * (centroid.x - corner.x) + bisDirY * (centroid.y - corner.y) > 0;
    const sign = pointsInward ? -1 : 1;
    const length = (sign * distance) / sinHalf;

    return {
      x: corner.x + bisDirX * length,
      y: corner.y + bisDirY * length,
    };
  });
}

/**
 * True when offsetting has pushed an edge past its opposite side: the edge then
 * runs backwards relative to the original outline. Signed area is no use here —
 * a triangle offset past its incentre keeps its winding, it just turns inside
 * out around the incentre.
 */
const hasReversedEdge = (
  vertices: Coordinate[],
  offsetVertices: Coordinate[]
): boolean =>
  vertices.some((vertex, i) => {
    const next = vertices[(i + 1) % vertices.length];
    const offsetVertex = offsetVertices[i];
    const offsetNext = offsetVertices[(i + 1) % offsetVertices.length];

    const dx = next.x - vertex.x;
    const dy = next.y - vertex.y;
    const offsetDx = offsetNext.x - offsetVertex.x;
    const offsetDy = offsetNext.y - offsetVertex.y;

    return dx * offsetDx + dy * offsetDy <= 0;
  });

/**
 * Like `offsetPolygon`, but returns null when the band is wider than the shape
 * and the offset outline would turn inside out.
 */
export function offsetPolygonSafe(
  vertices: Coordinate[],
  distance: number
): Coordinate[] | null {
  const offsetVertices = offsetPolygon(vertices, distance);
  return hasReversedEdge(vertices, offsetVertices) ? null : offsetVertices;
}

/**
 * The two faces of an element's thickness band, as offsets inward from the
 * stored geometry. Positive means "further into the shape".
 */
export function getThicknessFaces(element: Element): {
  inner: number;
  outer: number;
} {
  const thickness = getThickness(element);

  switch (getThicknessAnchor(element)) {
    case ThicknessAnchor.Outer:
      return { outer: 0, inner: thickness };
    case ThicknessAnchor.Inner:
      return { outer: -thickness, inner: 0 };
    default:
      return { outer: -thickness / 2, inner: thickness / 2 };
  }
}

/**
 * The closed outline of a shape, as opposed to its bounding box. Only the types
 * that carry a meaningful thickness band are covered.
 */
function getShapeOutline(element: Element): Coordinate[] | null {
  if (!isRectLikeElement(element)) return null;

  const {
    boundingBox: { tL, tR, bR, bL },
    shapeVertices,
  } = computeShapeVertices(element);

  if (element.type === ElementType.Rect) return [tL, tR, bR, bL];

  if (element.type === ElementType.Triangle) {
    const [apex] = shapeVertices;
    return [apex, bR, bL];
  }

  if (element.type === ElementType.Trapezoid) {
    const [tTL, tTR, tBL, tBR] = shapeVertices;
    return [tTL, tTR, tBR, tBL];
  }

  // An angle is open, so it has no closed outline to offset into faces.
  return null;
}

/**
 * Below this thickness the two faces sit within a pixel of each other, so
 * emitting both would only add snapping noise (and cost) for no visible gain.
 */
export const MIN_FACE_SNAP_THICKNESS = 2;

export const hasSnappableFaces = (element: Element): boolean =>
  getThickness(element) > MIN_FACE_SNAP_THICKNESS;

/**
 * The inner and outer faces of an element's thickness band as closed outlines.
 * Returns null when the element has no band worth snapping to, or when the band
 * is wider than the shape itself.
 */
export function getElementFaceOutlines(
  element: Element
): { inner: Coordinate[]; outer: Coordinate[] } | null {
  if (!hasSnappableFaces(element)) return null;

  const outline = getShapeOutline(element);
  if (!outline) return null;

  const faces = getThicknessFaces(element);
  // offsetPolygon takes an outward distance; face offsets are inward.
  const offset = (distance: number) =>
    element.type === ElementType.Rect
      ? offsetBox(outline, -distance)
      : offsetPolygonSafe(outline, -distance);

  const inner = offset(faces.inner);
  const outer = offset(faces.outer);
  if (!inner || !outer) return null;

  return { inner, outer };
}

/**
 * Axis-aligned boxes are offset analytically rather than through the bisector
 * construction, so a rect's faces land on exact coordinates and agree with the
 * rendered path.
 */
function offsetBox(
  [tL, , bR]: Coordinate[],
  distance: number
): Coordinate[] | null {
  const left = tL.x - distance;
  const top = tL.y - distance;
  const right = bR.x + distance;
  const bottom = bR.y + distance;

  const inverted =
    Math.sign(right - left) !== Math.sign(bR.x - tL.x) ||
    Math.sign(bottom - top) !== Math.sign(bR.y - tL.y);
  if (inverted) return null;

  return [
    { x: left, y: top },
    { x: right, y: top },
    { x: right, y: bottom },
    { x: left, y: bottom },
  ];
}

const outlineEdges = (outline: Coordinate[]): LineSegment[] =>
  outline.map((start, i) => ({
    start,
    end: outline[(i + 1) % outline.length],
  }));

export function getHandleButtonPosition(
  cappedScale: number,
  tL: Coordinate,
  tR: Coordinate,
  showTextEditHandler: boolean
) {
  const handleY = tL.y - 2 - 36 * cappedScale;
  const handleSize = ROTATE_HANDLE_SIZE_PX * cappedScale;
  const rotateX = showTextEditHandler
    ? (tL.x + tR.x) / 2 - 28 * cappedScale
    : (tL.x + tR.x - 12 * cappedScale) / 2;
  const editTextX = (tL.x + tR.x) / 2 + 4 * cappedScale;

  return { handleY, handleSize, rotateX, editTextX };
}

type BoundingBox = {
  tL: Coordinate;
  tR: Coordinate;
  bR: Coordinate;
  bL: Coordinate;
};

type ShapeVertices = {
  boundingBox: BoundingBox;
  shapeVertices: Coordinate[];
};

/**
 * Resolves a normalized template point to an absolute (unrotated) coordinate in
 * the fixture's local frame, applying horizontal/vertical mirroring.
 */
export const resolveFixturePoint = (
  e: FixtureElement,
  p: NormalizedPoint
): Coordinate => {
  const fx = e.mirroredX ? 1 - p.fx : p.fx;
  const fy = e.mirroredY ? 1 - p.fy : p.fy;
  return { x: e.x + fx * e.width, y: e.y + fy * e.height };
};

/**
 * Returns the extra fixture-specific snap points (unrotated) for a fixture,
 * resolved from its FixtureType template and current size/mirroring.
 */
export const getFixtureSnapPoints = (e: FixtureElement): Coordinate[] =>
  resolveSnapTemplate(
    fixtureRegistry[e.fixtureType].snapTemplate,
    e
  ).points.map((p) => resolveFixturePoint(e, p));

/**
 * Returns the extra fixture-specific snap edges (unrotated) for a fixture,
 * resolved from its FixtureType template and current size/mirroring.
 */
export const getFixtureSnapEdges = (e: FixtureElement): LineSegment[] =>
  resolveSnapTemplate(fixtureRegistry[e.fixtureType].snapTemplate, e).edges.map(
    (s) => ({
      start: resolveFixturePoint(e, s.start),
      end: resolveFixturePoint(e, s.end),
    })
  );

/**
 * Computes the unrotated vertex set of a rect-like element: the 4 bounding-box
 * corners plus any shape-specific vertices (triangle apex, trapezoid corners).
 * Single source of truth consumed by both getElementVertices and
 * getElementShapeEdges.
 */
function computeShapeVertices(e: RectLikeElement): ShapeVertices {
  const boundingBox: BoundingBox = {
    tL: { x: e.x, y: e.y },
    tR: { x: e.x + e.width, y: e.y },
    bR: { x: e.x + e.width, y: e.y + e.height },
    bL: { x: e.x, y: e.y + e.height },
  };

  const shapeVertices: Coordinate[] = [];

  if (e.type === ElementType.Triangle || e.type === ElementType.Angle) {
    const tri = e;
    shapeVertices.push({
      x: tri.x + tri.pointRatio * tri.width,
      y: tri.y,
    });
  } else if (e.type === ElementType.Trapezoid) {
    const trap = e;
    shapeVertices.push(
      { x: trap.x + (trap.width - trap.topWidth) / 2, y: trap.y },
      {
        x: trap.x + trap.topWidth + (trap.width - trap.topWidth) / 2,
        y: trap.y,
      },
      {
        x: trap.x + (trap.width - trap.bottomWidth) / 2,
        y: trap.y + trap.height,
      },
      {
        x: trap.x + trap.bottomWidth + (trap.width - trap.bottomWidth) / 2,
        y: trap.y + trap.height,
      }
    );
  } else if (e.type === ElementType.Fixture) {
    shapeVertices.push(...getFixtureSnapPoints(e));
  }

  return { boundingBox, shapeVertices };
}

/**
 * Returns the unrotated vertices of a rect-like element: 4 bounding-box corners
 * plus any shape-specific vertices (triangle apex, trapezoid corners).
 * Does not include the center — callers append it explicitly if needed.
 */
export function getElementVertices(element: Element): Coordinate[] {
  if (!isRectLikeElement(element)) {
    return [];
  }

  const {
    boundingBox: { tL, tR, bR, bL },
    shapeVertices,
  } = computeShapeVertices(element);

  const faces = getElementFaceOutlines(element);

  return [
    tL,
    tR,
    bR,
    bL,
    ...shapeVertices,
    ...(faces ? [...faces.inner, ...faces.outer] : []),
  ];
}

/**
 * Returns the unrotated edges of an element as LineSegments.
 * Includes both the axis-aligned bounding box edges and any shape-specific
 * edges (triangle sides, trapezoid top/bottom/slopes).
 * Rotation and extension are applied by the caller.
 */
export function getElementShapeEdges(element: RectLikeElement): LineSegment[] {
  const {
    boundingBox: { tL, tR, bR, bL },
    shapeVertices,
  } = computeShapeVertices(element);

  const faces = getElementFaceOutlines(element);
  const faceEdges: LineSegment[] = faces
    ? [...outlineEdges(faces.inner), ...outlineEdges(faces.outer)]
    : [];

  const boundingBoxEdges: LineSegment[] = [
    { start: tL, end: tR },
    { start: tR, end: bR },
    { start: bR, end: bL },
    { start: bL, end: tL },
  ];

  if (element.type === ElementType.Triangle) {
    const [apex] = shapeVertices;
    return [
      ...boundingBoxEdges,
      { start: bL, end: bR },
      { start: bL, end: apex },
      { start: bR, end: apex },
      ...faceEdges,
    ];
  }

  if (element.type === ElementType.Angle) {
    const [apex] = shapeVertices;
    return [
      ...boundingBoxEdges,
      { start: bL, end: apex },
      { start: bR, end: apex },
      ...faceEdges,
    ];
  }

  if (element.type === ElementType.Trapezoid) {
    const [tTL, tTR, tBL, tBR] = shapeVertices;
    return [
      ...boundingBoxEdges,
      { start: tTL, end: tTR },
      { start: tBL, end: tBR },
      { start: tTL, end: tBL },
      { start: tTR, end: tBR },
      ...faceEdges,
    ];
  }

  if (element.type === ElementType.Fixture) {
    return [...boundingBoxEdges, ...getFixtureSnapEdges(element)];
  }

  return [...boundingBoxEdges, ...faceEdges];
}

export function getElementAxisAlignedCorners(el: Element): [number, number][] {
  if (el.type === ElementType.Polyline) {
    return el.points.map((p) => [p.x, p.y]);
  }
  const { x, y, rotate } = el;
  const w = el.width;
  const h = el.height;
  const cx = x + w / 2;
  const cy = y + h / 2;
  const rawCorners: [number, number][] = [
    [x, y],
    [x + w, y],
    [x + w, y + h],
    [x, y + h],
  ];
  return rawCorners.map(
    ([px, py]) => rotateCenter(px, py, cx, cy, rotate) as [number, number]
  );
}

export const PERPENDICULAR_GUIDE_HALF_LENGTH = 120;

export type PerpGuideLine = { x1: number; y1: number; x2: number; y2: number };

// Computes the perpendicular guide lines for the first segment of a polyline.
// Returns one guide centred on each endpoint, both perpendicular to the
// segment p0 -> p1, or null when the segment is missing or zero-length.
export const getPerpendicularGuides = (
  processedPoints: [number, number, boolean][]
): PerpGuideLine[] | null => {
  const p0 = processedPoints[0];
  const p1 = processedPoints[1];
  if (!p0 || !p1) return null;

  const [x0, y0] = p0;
  const [x1, y1] = p1;
  const dx = x1 - x0;
  const dy = y1 - y0;
  const length = Math.hypot(dx, dy);

  if (length === 0) return null;

  // Unit vector perpendicular to the segment p0 -> p1.
  const perpX = -dy / length;
  const perpY = dx / length;

  const guideThrough = (px: number, py: number): PerpGuideLine => ({
    x1: px + perpX * PERPENDICULAR_GUIDE_HALF_LENGTH,
    y1: py + perpY * PERPENDICULAR_GUIDE_HALF_LENGTH,
    x2: px - perpX * PERPENDICULAR_GUIDE_HALF_LENGTH,
    y2: py - perpY * PERPENDICULAR_GUIDE_HALF_LENGTH,
  });

  return [guideThrough(x0, y0), guideThrough(x1, y1)];
};

export function computeMultiselectBBox(
  elements: Element[]
): { x: number; y: number; width: number; height: number } | null {
  if (elements.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const el of elements) {
    for (const [px, py] of getElementAxisAlignedCorners(el)) {
      if (px < minX) minX = px;
      if (py < minY) minY = py;
      if (px > maxX) maxX = px;
      if (py > maxY) maxY = py;
    }
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

// Handle sizing moved to ./handleScale; re-exported so existing call sites keep working.
export {
  getCappedHandleScale,
  getRoundHandleRadius,
  getSquareHandleSize,
} from './handleScale';
